import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

const PDFSlideViewer = ({ pdfData, pageNumber, width = null, height = null, maxWidth = null, maintainAspectRatio = true }) => {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber);
  const [nextPageNumber, setNextPageNumber] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const documentRef = useRef(null);
  const loadedPdfRef = useRef(null);
  
  // Memoize options
  const options = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@5.4.296/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@5.4.296/standard_fonts/',
    isEvalSupported: false,
  }), []);
  
  // Calculate initial width
  const getInitialWidth = () => {
    if (width) return width;
    if (typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      if (maxWidth) return Math.min(screenWidth - 64, maxWidth);
      return Math.min(screenWidth - 64, 800);
    }
    return 800;
  };
  
  const [containerWidth, setContainerWidth] = useState(getInitialWidth());

  // Validate PDF data
  const isValidPdfData = () => {
    if (!pdfData) return false;
    if (typeof pdfData !== 'string') return false;
    if (pdfData.length < 10) return false;
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    return base64Regex.test(pdfData);
  };

  // Handle page number changes
  useEffect(() => {
    if (pageNumber !== currentPageNumber && documentLoaded) {
      setNextPageNumber(pageNumber);
      setIsTransitioning(true);
    }
  }, [pageNumber, currentPageNumber, documentLoaded]);

  // Reset document state only when pdfData changes
  useEffect(() => {
    if (pdfData !== loadedPdfRef.current) {
      setDocumentLoaded(false);
      setError(null);
      setCurrentPageNumber(pageNumber);
      setNextPageNumber(null);
      setIsTransitioning(false);
      loadedPdfRef.current = pdfData;
      
      if (pdfData && !isValidPdfData()) {
        console.warn('PDFSlideViewer: Invalid PDF data format');
        setError('Invalid PDF data format');
      }
    }
  }, [pdfData]);

  // Handle responsive width
  useEffect(() => {
    if (width) {
      setContainerWidth(width);
      return;
    }

    const updateWidth = () => {
      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        if (availableWidth > 0) {
          const finalWidth = maxWidth ? Math.min(availableWidth, maxWidth) : availableWidth;
          setContainerWidth(finalWidth);
        }
      } else if (typeof window !== 'undefined') {
        const screenWidth = window.innerWidth;
        const finalWidth = maxWidth ? Math.min(screenWidth - 64, maxWidth) : screenWidth - 64;
        setContainerWidth(finalWidth);
      }
    };

    const timeoutId = setTimeout(updateWidth, 50);
    const handleResize = () => updateWidth();
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [width, maxWidth]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setError(null);
    setDocumentLoaded(true);
    console.log('✓ PDF Document loaded, pages:', numPages);
  }

  function onDocumentLoadError(err) {
    console.error('✗ PDF load error:', err);
    const errorMessage = err?.message || 'Failed to load PDF';
    setError(errorMessage);
    setDocumentLoaded(false);
  }

  function onPageRenderSuccess() {
    if (isTransitioning && nextPageNumber !== null) {
      setCurrentPageNumber(nextPageNumber);
      setNextPageNumber(null);
      setIsTransitioning(false);
    }
  }

  if (!pdfData) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-slate-50 rounded-lg">
        <p className="text-slate-500">No PDF uploaded</p>
      </div>
    );
  }

  if (!isValidPdfData()) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-red-50 rounded-lg p-4">
        <div className="text-center">
          <p className="text-red-600 mb-2">Invalid PDF data</p>
          <p className="text-xs text-slate-500">The PDF data format is not valid</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-red-50 rounded-lg p-4">
        <div className="text-center">
          <p className="text-red-600 mb-2">{error}</p>
          <p className="text-xs text-slate-500">Unable to display PDF</p>
        </div>
      </div>
    );
  }

  const pdfDataUrl = useMemo(() => {
    try {
      return `data:application/pdf;base64,${pdfData}`;
    } catch (err) {
      console.error('Error creating PDF data URL:', err);
      return null;
    }
  }, [pdfData]);

  if (!pdfDataUrl) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-red-50 rounded-lg p-4">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error processing PDF</p>
          <p className="text-xs text-slate-500">Could not create PDF data URL</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="relative flex items-center justify-center bg-white rounded-lg w-full"
    >
      <Document
        ref={documentRef}
        file={pdfDataUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={options}
        loading={<div className="min-h-[200px]" />}
        error={
          <div className="flex items-center justify-center p-8 min-h-[200px]">
            <p className="text-red-600">Error loading PDF document</p>
          </div>
        }
      >
        {/* Current visible page */}
        {documentLoaded && (height > 0 || containerWidth > 0) && numPages && currentPageNumber <= numPages && (
          <div 
            className="relative transition-opacity duration-200"
            style={{ opacity: isTransitioning ? 0.7 : 1 }}
          >
            <Page 
              pageNumber={currentPageNumber}
              {...(height ? { height: height } : { width: containerWidth })}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={<div className="min-h-[200px]" />}
            />
          </div>
        )}
        
        {/* Next page preloading */}
        {isTransitioning && nextPageNumber && documentLoaded && (height > 0 || containerWidth > 0) && numPages && nextPageNumber <= numPages && (
          <div className="absolute top-0 left-0 opacity-0 pointer-events-none">
            <Page 
              pageNumber={nextPageNumber}
              {...(height ? { height: height } : { width: containerWidth })}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onRenderSuccess={onPageRenderSuccess}
              loading={<div className="min-h-[200px]" />}
            />
          </div>
        )}
      </Document>
    </div>
  );
};

export default PDFSlideViewer;
